/**
 * Copyright & Source Attribution Modal
 * Provides comprehensive legal, scientific, and educational attributions
 * for NASA datasets, imagery, mission archives, and open-source libraries.
 */

export class CreditsModal {
  constructor(container) {
    this.container = container;

    this.modal = document.createElement('div');
    this.modal.className = 'recon-modal-backdrop hidden';
    this.container.appendChild(this.modal);

    this.render();
    this.initEvents();
  }

  render() {
    this.modal.innerHTML = `
      <div class="recon-modal-content glass-card credits-modal-content">
        <div class="recon-modal-header">
          <div class="title-wrap">
            <span class="badge">LEGAL & CITATIONS</span>
            <h2>Copyright & Source Attribution</h2>
          </div>
          <button class="close-btn" id="credits-close" aria-label="Close Credits">&times;</button>
        </div>

        <div class="credits-body">
          <!-- Application Copyright & Composite License -->
          <div class="credits-card highlight-card">
            <h3>Composite Licensing Structure</h3>
            <ul class="composite-license-list" style="list-style: none; margin-top: 8px; display: flex; flex-direction: column; gap: 6px;">
              <li>&bull; <strong>Application Source Code:</strong> Licensed under GNU Affero General Public License v3.0 (AGPL-3.0).</li>
              <li>&bull; <strong>Third-Party Libraries:</strong> Three.js (MIT License), Vite (MIT License).</li>
              <li>&bull; <strong>Planetary Imagery & Elevation:</strong> NASA / LROC / LOLA (Public Domain under 17 U.S.C. &sect; 105).</li>
            </ul>
          </div>

          <!-- NASA Imagery & Scientific Data Sources -->
          <div class="credits-section">
            <h3>NASA Scientific Data & Imagery Sources</h3>
            <ul class="credits-list">
              <li>
                <strong>LROC (Lunar Reconnaissance Orbiter Camera)</strong><br />
                Global Wide-Angle Camera (WAC) 100m/pixel photometric mosaic and Narrow-Angle Camera (NAC) sub-meter surface reconnaissance imagery.
                Courtesy of <em>NASA / Goddard Space Flight Center (GSFC) / Arizona State University (ASU)</em>.
              </li>
              <li>
                <strong>LOLA (Lunar Orbiter Laser Altimeter)</strong><br />
                Global Digital Elevation Models (DEM) and topographic elevation grids.
                Courtesy of <em>NASA / GSFC / LOLA Science Team</em>.
              </li>
              <li>
                <strong>CGI Moon Kit (SVS ID: 4720)</strong><br />
                High-resolution 3D albedo and displacement maps developed by Ernie Wright.
                Courtesy of <em>NASA Scientific Visualization Studio (SVS)</em>.
              </li>
              <li>
                <strong>Public Domain Notice</strong><br />
                NASA photographic and cartographic materials are not protected by copyright in the United States and are in the public domain under <strong>17 U.S.C. &sect; 105</strong>.
              </li>
            </ul>
          </div>

          <!-- Mission Archives & Historical Photography -->
          <div class="credits-section">
            <h3>Mission Archives & International Agencies</h3>
            <ul class="credits-list">
              <li>
                <strong>Apollo Program Surface Photography (1969–1972)</strong><br />
                Apollo 11, 12, 14, 15, 16, and 17 astronaut photography, EVA traverse maps, and sample logs courtesy of the <em>NASA History Division</em>, <em>Lyndon B. Johnson Space Center (JSC)</em>, and the <em>Apollo Lunar Surface Journal (ALSJ)</em>.
              </li>
              <li>
                <strong>ISRO (Indian Space Research Organisation)</strong><br />
                Chandrayaan-3 Vikram lander and Pragyan rover mission coordinates and lunar southern latitude data.
              </li>
              <li>
                <strong>CNSA (China National Space Administration)</strong><br />
                Chang'e-4 and Chang'e-6 farside landing site coordinates and South Pole-Aitken basin exploratory datasets.
              </li>
              <li>
                <strong>Soviet Lunar Program (Luna Series)</strong><br />
                Historic Luna 2, Luna 9, and Luna 16 robotic landing and sample return coordinates via the <em>NASA Space Science Data Coordinated Archive (NSSDCA)</em>.
              </li>
            </ul>
          </div>

          <!-- Open-Source Libraries & Software Attribution -->
          <div class="credits-section">
            <h3>Open-Source Software Attributions</h3>
            <ul class="credits-list">
              <li>
                <strong>Three.js</strong> — Licensed under the MIT License.<br />
                Copyright &copy; 2010–2026 Three.js Authors (Ricardo Cabello / Mr.doob).
              </li>
              <li>
                <strong>Vite</strong> — Licensed under the MIT License.<br />
                Copyright &copy; 2019–present Evan You &amp; Vite contributors.
              </li>
            </ul>
          </div>
        </div>

        <div class="credits-footer">
          <button class="action-btn primary" id="credits-dismiss-btn">Close Attribution</button>
        </div>
      </div>
    `;
  }

  initEvents() {
    this.modal.querySelector('#credits-close').addEventListener('click', () => this.hide());
    this.modal.querySelector('#credits-dismiss-btn').addEventListener('click', () => this.hide());

    this.modal.addEventListener('click', (e) => {
      if (e.target === this.modal) this.hide();
    });
  }

  show() {
    this.modal.classList.remove('hidden');
  }

  hide() {
    this.modal.classList.add('hidden');
  }

  toggle() {
    this.modal.classList.toggle('hidden');
  }
}
